import { useEffect, useState } from 'preact/hooks';
import type { RefObject } from 'preact';
import { logError } from '@/sdkConsole';

// Slot names must be unique per mount: surfaces can overlap (e.g. a
// dismissible paywall left open while openCheckout mounts a purchase), and two
// containers sharing a slot name would both render into the first slot.
let slotCounter = 0;

function slotHost(anchor: HTMLElement): HTMLElement {
  const root = anchor.getRootNode();
  return root instanceof ShadowRoot ? (root.host as HTMLElement) : document.body;
}

// useStripeElementSlot mounts a Stripe Element (or the GIS button, which has
// the same constraint) into a light-DOM container projected into the shadow
// tree with a <slot>. Stripe Elements cannot render inside a shadow root —
// Stripe.js reaches its iframes through window.frames, which does not
// penetrate shadow boundaries — so the mount node must live in the light DOM.
// A slotted light-DOM child of the shadow host is the Stripe-recommended
// workaround: it participates in the flat tree at the slot position, so it
// scrolls with the modal content and — unlike a fixed-position portal — a
// touch/wheel scroll starting on the iframe chains up to the .sm-overlay
// scroller instead of dead-ending at the scroll-locked body.
//
// `anchorRef` must point at an element rendered inside the widget's shadow
// tree; it locates the shadow host the container is appended to. When the
// anchor is not rendered (the component returned null, e.g. GIS self-hidden),
// no slot target exists, so nothing is mounted — falling back to body there
// would leave a stray empty container on the publisher page. (Unit tests
// render into plain light DOM, where the fallback is document.body — the slot
// attribute is inert there and the container renders as an ordinary in-flow
// div, which is fine since those tests stub the element and it stays empty.)
//
// Returns the slot name; the component renders
// `<div class="sm-payment-slot"><slot name={slotName} /></div>` where the
// element should appear. `mount` receives the container and a `cancelled`
// getter (true once the effect tears down — guard async setState against it).
// It loads/creates the Element, mounts it into the container, and returns a
// cleanup (e.g. element.destroy) or nothing. The effect re-runs when `deps`
// change.
export function useStripeElementSlot(
  anchorRef: RefObject<HTMLElement>,
  mount: (mountDiv: HTMLDivElement, cancelled: () => boolean) => Promise<(() => void) | void>,
  deps: unknown[],
): string {
  const [slotName] = useState(() => `sm-light-dom-${++slotCounter}`);

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) {
      return;
    }
    const host = slotHost(anchor);

    const container = document.createElement('div');
    container.setAttribute('slot', slotName);
    // The container is light DOM, so publisher page CSS (element selectors
    // like `div { margin: … }`) can reach it. Reset it to a plain block box;
    // the Stripe iframe inside is cross-origin and unaffected. `all` does not
    // touch custom properties, so --sm-* tokens still inherit through the
    // flat tree.
    container.style.cssText = 'all: initial; display: block;';
    host.appendChild(container);

    let cancelled = false;
    let cleanupElement: (() => void) | void;
    Promise.resolve(mount(container, () => cancelled))
      .then((cleanup) => {
        // Unmounted while the Element was still loading: run its cleanup and
        // stop — the effect's teardown already ran with nothing to release.
        if (cancelled) {
          cleanup?.();
          return;
        }
        cleanupElement = cleanup;
      })
      // mount surfaces expected load failures (e.g. Stripe.js) via its own
      // component state; an unexpected rejection past that has no UI, so log it.
      .catch((err) => {
        logError('failed to mount the slotted element', err);
      });

    return () => {
      cancelled = true;
      cleanupElement?.();
      container.remove();
    };
    // slotName is stable for the component's lifetime; deps drives re-runs.
  }, deps);

  return slotName;
}

// The light-DOM node useStripeElementSlot appended for `slotName`, or null if
// the slot has not been allocated (no anchor, or the effect has not run).
// Look this up at mount time rather than stashing the node in a ref: a stale
// slot-effect cleanup can null a shared ref after a newer effect has already
// assigned the replacement container.
export function lightDomSlotContainer(anchor: HTMLElement | null, slotName: string): HTMLDivElement | null {
  if (!anchor) {
    return null;
  }
  return slotHost(anchor).querySelector(`[slot="${slotName}"]`);
}
